import { useCallback, useMemo } from 'react'
import {
  getDownloadUrl,
  getFormatIndex,
  getFormatLabel,
  getResolutionIndex,
  getResolutionLabelByDimensions,
} from '@/lib/assets/assets'
import { useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { Asset, AssetSummary } from '@/lib/assets/types'

export type FormatOption = {
  label: string
  value: string
}

export type ResolutionOption = {
  label: string
  value: string
}

export type DownloadOption = {
  url: string
  resolution?: string
  format?: string
  size?: number
  level?: string | number
}

export default function useAssetDownload({ asset, userToken }: { asset?: Asset | AssetSummary; userToken?: string }) {
  const resolutionIndex = useMemo(() => (!!asset ? getResolutionIndex(asset) : -1), [asset])
  const formatIndex = useMemo(() => (!!asset ? getFormatIndex(asset) : -1), [asset])
  const appEvent$ = useEventEmitterContext()

  const resolutions: ResolutionOption[] = useMemo(() => {
    if (!asset || !('dl_opts' in asset) || !asset.dl_opts || resolutionIndex === -1) {
      return []
    }

    // Extract the resolution values from the asset.dl_opts.items, preventing duplicates.
    const resolutionValues =
      asset.dl_opts?.items
        ?.map((item) => item.variant_labels[resolutionIndex])
        .filter((value) => !!value)
        .filter((value, index, self) => self.findIndex((v) => v === value) === index) ?? []

    return resolutionValues.map((value) => {
      return {
        label: getResolutionLabelByDimensions(value, {
          appendDimensions: true,
          originalDimensions: 'dimensions' in asset ? asset.dimensions : undefined,
        }),
        value: value,
      }
    })
  }, [asset, resolutionIndex])

  const formats: FormatOption[] = useMemo(() => {
    if (!asset || !('dl_opts' in asset) || !asset.dl_opts || formatIndex === -1) {
      return []
    }

    // Extract the format values from the asset.dl_opts.items, preventing duplicates.
    const formatValues =
      asset.dl_opts?.items
        ?.map((item) => item.variant_labels[formatIndex])
        .filter((value) => !!value)
        .filter((value, index, self) => self.findIndex((v) => v === value) === index) ?? []

    return formatValues.map((value) => {
      return {
        label: getFormatLabel(value),
        value: value,
      }
    })
  }, [asset, formatIndex])

  const downloadOptions: DownloadOption[] = useMemo(() => {
    if (!asset || !('dl_opts' in asset) || !asset.dl_opts) {
      return []
    }

    return asset.dl_opts.items.map((downloadItem) => {
      const resolution = resolutionIndex !== -1 ? downloadItem.variant_labels[resolutionIndex] : undefined
      const format = formatIndex !== -1 ? downloadItem.variant_labels[formatIndex] : undefined
      const url = getDownloadUrl({ asset, resolution, format, userToken }) ?? '#'

      return {
        url,
        resolution: resolution
          ? getResolutionLabelByDimensions(resolution, {
              appendDimensions: true,
              originalDimensions: 'dimensions' in asset ? asset.dimensions : undefined,
            })
          : undefined,
        format: format ? getFormatLabel(format) : undefined,
        size: downloadItem.size ?? undefined,
        level: downloadItem.level ?? undefined,
      }
    })
  }, [asset, formatIndex, resolutionIndex, userToken])

  const dispatchAssetDownloadEvent = useCallback(() => {
    appEvent$.emit({
      type: 'asset:download',
      payload: asset,
    })
  }, [appEvent$, asset])

  return {
    resolutionIndex,
    formatIndex,
    resolutions,
    formats,
    downloadOptions,
    dispatchAssetDownloadEvent,
  }
}
